import { useMemo, useState } from 'react'
import { downloadFile } from '../api/client'
import { Button } from '../components/ui/Button'
import { Card, CardBody, CardHeader } from '../components/ui/Card'
import { EmptyState } from '../components/ui/EmptyState'
import { Field, Select } from '../components/ui/Field'
import { DateInput } from '../components/ui/DateTimeInputs'
import { Badge } from '../components/ui/Badge'
import { PageLoader } from '../components/ui/Spinner'
import {
    summaryQueryString,
    useClients,
    useProjects,
    useSummary,
    type SummaryFilters,
    type SummaryGroupBy,
} from '../hooks/useApi'
import {
    formatDate,
    formatDurationCompact,
    formatMoney,
    fromLocalInputValue,
    startOfWeekISO,
    toLocalInputValue,
} from '../lib/format'
import { useT } from '../i18n/context'
import type { DictPath } from '../i18n/translate'
import { toast } from '../stores/toast'
import { useActiveWorkspaceId } from '../stores/workspace'

type Preset = 'week' | 'month' | 'year' | 'all' | 'custom'
type ReportGroupBy = Exclude<SummaryGroupBy, 'none'>

const presetKeys: Record<Preset, DictPath> = {
    week: 'reports.presetWeek',
    month: 'reports.presetMonth',
    year: 'reports.presetYear',
    all: 'reports.presetAll',
    custom: 'reports.presetCustom',
}

const groupKeys: Record<ReportGroupBy, DictPath> = {
    project: 'reports.groupProject',
    client: 'reports.groupClient',
    user: 'reports.groupUser',
    day: 'reports.groupDay',
}

function ymd(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function presetRange(preset: Preset): { from: string; to: string } | null {
    const now = new Date()
    switch (preset) {
        case 'week':
            return {
                from: toLocalInputValue(startOfWeekISO()).slice(0, 10),
                to: ymd(now),
            }
        case 'month':
            return {
                from: ymd(new Date(now.getFullYear(), now.getMonth(), 1)),
                to: ymd(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
            }
        case 'year':
            return {
                from: `${now.getFullYear()}-01-01`,
                to: `${now.getFullYear()}-12-31`,
            }
        default:
            return null
    }
}

export function ReportsPage() {
    const t = useT()
    const workspaceId = useActiveWorkspaceId()
    const clients = useClients(workspaceId)
    const projects = useProjects(workspaceId)

    const [preset, setPreset] = useState<Preset>('month')
    const [from, setFrom] = useState('')
    const [to, setTo] = useState('')
    const [clientId, setClientId] = useState<number | ''>('')
    const [projectId, setProjectId] = useState<number | ''>('')
    const [groupBy, setGroupBy] = useState<ReportGroupBy>('project')

    const customRange = preset === 'custom'
    const range = customRange
        ? { from, to }
        : (presetRange(preset) ?? { from: '', to: '' })

    const filters = useMemo<SummaryFilters>(() => {
        const f: SummaryFilters = { groupBy }
        if (range.from)
            f.startDate = fromLocalInputValue(`${range.from}T00:00`)
        if (range.to) f.endDate = fromLocalInputValue(`${range.to}T23:59`)
        if (clientId) f.clientId = clientId
        if (projectId) f.projectId = projectId
        return f
    }, [range.from, range.to, clientId, projectId, groupBy])

    const summary = useSummary(workspaceId, filters)

    const projectOptions = (projects.data ?? []).filter(
        p => clientId === '' || p.clientId === clientId,
    )

    const choosePreset = (next: Preset) => {
        setPreset(next)
        if (next === 'custom' && !from && !to) {
            const month = presetRange('month')!
            setFrom(month.from)
            setTo(month.to)
        }
    }

    const groups = summary.data?.groups ?? []

    const exportCsv = () => {
        downloadFile(
            `/time-entries/export?${summaryQueryString(workspaceId!, filters)}`,
            `${t('reports.csvPrefix')}-${t(groupKeys[groupBy]).toLowerCase()}.csv`,
        ).catch(() => toast.error(t('common.exportFailed')))
    }

    return (
        <div className='space-y-4'>
            <Card>
                <CardBody className='space-y-3'>
                    <div className='flex flex-wrap items-end gap-2'>
                        <Field label={t('reports.period')}>
                            <div className='flex overflow-hidden rounded-lg border border-slate-300 dark:border-slate-600'>
                                {(Object.keys(presetKeys) as Preset[]).map(id => (
                                    <button
                                        key={id}
                                        type='button'
                                        onClick={() => choosePreset(id)}
                                        className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${
                                            preset === id
                                                ? 'bg-indigo-700 dark:bg-indigo-600 text-white'
                                                : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/60'
                                        }`}
                                    >
                                        {t(presetKeys[id])}
                                    </button>
                                ))}
                            </div>
                        </Field>
                        {customRange && (
                            <>
                                <Field label={t('entries.from')}>
                                    <DateInput
                                        value={from}
                                        onChange={setFrom}
                                        className='w-40'
                                    />
                                </Field>
                                <Field label={t('entries.to')}>
                                    <DateInput
                                        value={to}
                                        onChange={setTo}
                                        className='w-40'
                                    />
                                </Field>
                            </>
                        )}
                        <Field label={t('entries.client')}>
                            <Select
                                value={clientId}
                                onChange={e => {
                                    setClientId(
                                        e.target.value
                                            ? Number(e.target.value)
                                            : '',
                                    )
                                    setProjectId('')
                                }}
                                className='w-44'
                            >
                                <option value=''>{t('common.all')}</option>
                                {(clients.data ?? []).map(c => (
                                    <option key={c.id} value={c.id}>
                                        {c.name}
                                    </option>
                                ))}
                            </Select>
                        </Field>
                        <Field label={t('entries.project')}>
                            <Select
                                value={projectId}
                                onChange={e =>
                                    setProjectId(
                                        e.target.value
                                            ? Number(e.target.value)
                                            : '',
                                    )
                                }
                                className='w-44'
                            >
                                <option value=''>{t('common.all')}</option>
                                {projectOptions.map(p => (
                                    <option key={p.id} value={p.id}>
                                        {p.name}
                                    </option>
                                ))}
                            </Select>
                        </Field>
                        <Button
                            variant='secondary'
                            className='ml-auto'
                            onClick={exportCsv}
                        >
                            {t('entries.csvExport')}
                        </Button>
                    </div>
                    <div className='flex flex-wrap items-center gap-2'>
                        <span className='text-xs font-medium text-slate-500 dark:text-slate-400 uppercase'>
                            {t('reports.groupBy')}
                        </span>
                        {(Object.keys(groupKeys) as ReportGroupBy[]).map(g => (
                            <button
                                key={g}
                                type='button'
                                onClick={() => setGroupBy(g)}
                                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                                    groupBy === g
                                        ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-800 dark:text-indigo-300 ring-1 ring-indigo-600'
                                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                                }`}
                            >
                                {t(groupKeys[g])}
                            </button>
                        ))}
                    </div>
                </CardBody>
            </Card>

            {summary.isLoading ? (
                <Card>
                    <PageLoader />
                </Card>
            ) : summary.isError ? (
                <Card>
                    <EmptyState
                        title={t('reports.loadErrorTitle')}
                        description={t('reports.loadErrorDesc')}
                    />
                </Card>
            ) : (
                <>
                    <div className='grid grid-cols-2 gap-3 md:grid-cols-4'>
                        <KpiTile
                            label={t('reports.total')}
                            value={formatDurationCompact(
                                summary.data?.totalSeconds ?? 0,
                            )}
                        />
                        <KpiTile
                            label={t('reports.billable')}
                            value={formatDurationCompact(
                                summary.data?.billableSeconds ?? 0,
                            )}
                        />
                        <KpiTile
                            label={t('reports.earnings')}
                            value={formatMoney(summary.data?.earnings ?? 0)}
                        />
                        <KpiTile
                            label={t('reports.entriesLabel')}
                            value={String(summary.data?.entryCount ?? 0)}
                        />
                    </div>
                    {(summary.data?.runningCount ?? 0) > 0 && (
                        <div className='flex items-center gap-2 rounded-lg bg-amber-50 dark:bg-amber-950/60 px-4 py-2.5 text-sm text-amber-800 dark:text-amber-300'>
                            <Badge tone='amber'>{t('reports.note')}</Badge>
                            {(summary.data?.runningCount ?? 0) === 1
                                ? t('reports.runningOne')
                                : t('reports.runningMany', {
                                      count: summary.data?.runningCount ?? 0,
                                  })}
                        </div>
                    )}

                    <Card>
                        <CardHeader
                            title={t('reports.groupingTitle', {
                                group: t(groupKeys[groupBy]),
                            })}
                        />
                        {groups.length === 0 ? (
                            <EmptyState
                                title={t('reports.emptyTitle')}
                                description={t('reports.emptyDesc')}
                            />
                        ) : (
                            <CardBody className='p-0'>
                                <div className='overflow-x-auto'>
                                    <table className='w-full text-sm'>
                                        <thead>
                                            <tr className='border-b border-slate-200 dark:border-slate-700 text-left text-xs tracking-wide text-slate-500 dark:text-slate-400 uppercase'>
                                                <th className='px-4 py-2.5'>
                                                    {t(groupKeys[groupBy])}
                                                </th>
                                                <th className='px-4 py-2.5 text-right'>
                                                    {t('reports.time')}
                                                </th>
                                                <th className='px-4 py-2.5 text-right'>
                                                    {t('reports.billable')}
                                                </th>
                                                <th className='px-4 py-2.5 text-right'>
                                                    {t('reports.earnings')}
                                                </th>
                                                <th className='px-4 py-2.5 text-right'>
                                                    {t('reports.entriesLabel')}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {groups.map(g => (
                                                <tr
                                                    key={g.key}
                                                    className='border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50/70 dark:hover:bg-slate-800/60'
                                                >
                                                    <td className='px-4 py-2.5 font-medium text-slate-700 dark:text-slate-200'>
                                                        <GroupLabelCell
                                                            label={g.label}
                                                            groupBy={groupBy}
                                                        />
                                                    </td>
                                                    <td className='px-4 py-2.5 text-right text-slate-600 dark:text-slate-300 tabular-nums'>
                                                        {formatDurationCompact(
                                                            g.seconds,
                                                        )}
                                                    </td>
                                                    <td className='px-4 py-2.5 text-right text-slate-600 dark:text-slate-300 tabular-nums'>
                                                        {formatDurationCompact(
                                                            g.billableSeconds,
                                                        )}
                                                    </td>
                                                    <td className='px-4 py-2.5 text-right text-slate-600 dark:text-slate-300 tabular-nums'>
                                                        {formatMoney(
                                                            g.earnings,
                                                        )}
                                                    </td>
                                                    <td className='px-4 py-2.5 text-right text-slate-500 dark:text-slate-400 tabular-nums'>
                                                        {g.entryCount}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </CardBody>
                        )}
                    </Card>
                </>
            )}
        </div>
    )
}

function GroupLabelCell({
    label,
    groupBy,
}: {
    label: string
    groupBy: ReportGroupBy
}) {
    const t = useT()
    // 'YYYY-MM-DDT00:00:00' ohne Z → lokal interpretiert, kein UTC-Off-by-one
    if (groupBy === 'day')
        return <>{formatDate(`${label}T00:00:00`)}</>
    return <>{label || t('reports.noGroup')}</>
}

function KpiTile({ label, value }: { label: string; value: string }) {
    return (
        <div className='rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm'>
            <p className='text-xs font-medium tracking-wide text-slate-500 dark:text-slate-400 uppercase'>
                {label}
            </p>
            <p className='mt-1 text-2xl font-bold text-slate-800 dark:text-slate-100 tabular-nums'>
                {value}
            </p>
        </div>
    )
}
